import { PRICING } from "./pricing.js";

// Homepage FAQ — single source of truth for the visible FAQ (App.jsx renders
// these questions with JSX answers) and the FAQPage JSON-LD that ships in the
// static index.html <head> (see index.html markers + scripts/prerender-routes.mjs,
// which regenerates the block at build time so it can never drift).
//
// Content per docs/learn/learn-page-specs-batch1-2026-07-27.md (Tier 1, item 8),
// with compliance-checker notes applied (2026-07-27).
// `text` must stay a faithful plain-text mirror of the rendered answer —
// Google requires FAQ structured data to match the visible content.

export const HOME_FAQS = [
  {
    q: "How does Vyvron work?",
    text: "Vyvron is a telehealth access platform, not a medical provider or pharmacy. You complete a private online evaluation, and an independent licensed medical provider reviews your health history to determine whether prescription treatment is clinically appropriate. If a provider prescribes treatment, your medication is filled and shipped by a licensed pharmacy.",
  },
  {
    q: "Who prescribes the medication?",
    text: "Independent licensed medical providers — not Vyvron. Vyvron connects you with providers licensed in your state; all eligibility, medication, and treatment decisions are made solely by those providers. Prescription treatment is not guaranteed.",
  },
  {
    q: "Are these medications FDA-approved?",
    text: "Some medications available through our provider network may be compounded. Compounded medications are not FDA-approved and are not identical or equivalent to any FDA-approved branded medication. An independent licensed provider determines which option, if any, is clinically appropriate for you.",
  },
  {
    q: "What states is Vyvron available in?",
    text: "Vyvron connects patients with licensed providers in all 50 states plus Washington, D.C. Available nationwide; in certain states, evaluation requires a scheduled video visit (AR, DC, DE, KS, MS, NM, RI, WV). Compounded GLP-1 medications are not available to Mississippi residents; Mississippi residents may be eligible for branded options only. State, medication, and pricing availability may vary and can change.",
  },
  {
    q: "How does billing work?",
    text: `${PRICING.medicalWeightLoss.founding.display} every 4 weeks is a limited-time offer; our regular price is ${PRICING.medicalWeightLoss.standard.display} every 4 weeks. Your plan auto-renews at ${PRICING.medicalWeightLoss.founding.display} until you cancel. There are no hidden membership fees. You can cancel anytime by emailing support@vyvron.com; cancellation takes effect at the end of your current billing cycle, not immediately.`,
  },
  {
    q: "Is Vyvron legitimate?",
    text: "Yes. Vyvron is LegitScript-certified, meaning the platform has been independently vetted against telehealth compliance standards. You can verify the certification yourself — read our guide on how to verify a LegitScript certification at vyvron.com/learn/what-is-legitscript-certification, or click the LegitScript seal in our site footer.",
  },
];

// Marker comments that delimit the FAQ JSON-LD block inside index.html.
export const FAQ_JSONLD_START = "<!-- home-faq-jsonld:start -->";
export const FAQ_JSONLD_END = "<!-- home-faq-jsonld:end -->";

export function faqJsonLd() {
  return JSON.stringify(
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: HOME_FAQS.map(({ q, text }) => ({
        "@type": "Question",
        name: q,
        acceptedAnswer: { "@type": "Answer", text },
      })),
    },
    null,
    2,
  );
}

// Full block (markers + script tag) as it appears in index.html.
export function faqJsonLdBlock() {
  return `${FAQ_JSONLD_START}
    <script type="application/ld+json">
${faqJsonLd()
  .split("\n")
  .map((l) => "      " + l)
  .join("\n")}
    </script>
    ${FAQ_JSONLD_END}`;
}
